import { forwardRef, useId } from 'react';
import clsx from 'clsx';

const Input = forwardRef(({
  label,
  error,
  hint,
  required,
  className,
  inputClassName,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  ...props
}, ref) => {
  const reactId = useId();
  const inputId = id || `input-${reactId}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const describedBy = clsx(ariaDescribedBy, hint && hintId, error && errorId) || undefined;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && !String(label).includes('*') && (
            <span aria-hidden="true" className="ml-0.5 font-normal text-danger-500">*</span>
          )}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        required={required}
        aria-label={!label ? ariaLabel : undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        className={clsx(
          'input-field',
          error && 'border-danger-500 focus:ring-danger-500 focus:border-danger-500',
          // Consumer className is merged last so it wins on class-attribute order.
          inputClassName
        )}
        {...props}
      />
      {hint && <p id={hintId} className="mt-1.5 text-sm text-slate-500">{hint}</p>}
      {error && (
        <div role="alert">
          <p id={errorId} className="mt-1.5 text-sm font-medium text-danger-700">{error}</p>
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';
export default Input;
